import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SparklesIcon, HeartIcon, CurrencyDollarIcon, StarIcon, EyeIcon, SunIcon, BookOpenIcon, ScaleIcon, HashtagIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import HeroSection from '../components/HeroSection';
import { useThemeImages } from '../hooks/useThemeImages';
import { useThemeTypes } from '../hooks/useThemeTypes';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../hooks/useAuth';
import { toAbs } from '../utils/url';
import api from '../services/api';
import NavBar from '../components/NavBar';

// Rota de destino de cada feature (na ordem dos cards)
const FEATURE_ROUTES = [
  '/tarot',        // Tarot Rider-Waite
  '/astrologia',   // Astrologia Psicológica
  '/jung',         // Psicologia & Jung
  '/kabbalah',     // Arquétipos e Simbólicos
  '/amor',         // Amor e Relacionamentos
  '/prosperidade', // Prosperidade e Propósito
  '/bem-estar',    // Bem Estar e Saúde Emocional
  '/numerologia',  // Numerologia e Kabbalah
  '/karma',        // Karma e Destino
  '/caminhos',     // Caminhos Espirituais
];

const HomePage: React.FC = () => {
  const { themeImages } = useThemeImages();
  const { themeTypes, isLoading: themesLoading } = useThemeTypes();
  const { theme: themeConfig } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [heroBg, setHeroBg] = useState<string | null>(null);
  const [purchasingIdx, setPurchasingIdx] = useState<number | null>(null);

  const handleFeatureClick = (index: number) => {
    const route = FEATURE_ROUTES[index] ?? '/tarot';
    if (user) {
      navigate(route);
    } else {
      navigate('/login', { state: { from: route } });
    }
  };

  useEffect(() => {
    let alive = true;
    api.get('/hero/background')
      .then(r => r.data)
      .then(d => {
        if (!alive) return;
        // d.url já vem absoluta quando a imagem está no Cloudinary (caso
        // comum hoje) ou relativa quando é asset servido pelo próprio
        // backend — toAbs() já resolve os dois casos corretamente; NÃO
        // prefixar com getApiOrigin() aqui de novo, senão vira
        // "https://apihttps://res.cloudinary.com/..." (bug real: quebrava
        // o hero sempre que a imagem já veio de URL absoluta do Cloudinary).
        setHeroBg(d?.url || null);
      })
      .catch(() => { if (alive) setHeroBg(null); });
    return () => { alive = false; };
  }, []);
  
  const heroImage = toAbs(heroBg);

  // Fallback para temas estáticos caso não consiga carregar do backend
  const fallbackThemes = [
    {
      key: 'amor',
      name: 'Amor',
      description: 'Descubra os segredos do seu coração e encontre respostas sobre relacionamentos, paixões e conexões profundas.',
      icon: 'Heart',
      gradient: 'from-pink-500/20 to-rose-500/20'
    },
    {
      key: 'saude',
      name: 'Saúde',
      description: 'Obtenha orientações sobre bem-estar físico, mental e espiritual para uma vida mais equilibrada e saudável.',
      icon: 'Leaf',
      gradient: 'from-green-500/20 to-emerald-500/20'
    },
    {
      key: 'dinheiro',
      name: 'Dinheiro',
      description: 'Explore oportunidades financeiras, investimentos e caminhos para a prosperidade e abundância material.',
      icon: 'DollarSign',
      gradient: 'from-yellow-500/20 to-orange-500/20'
    }
  ];

  // Usar temas dinâmicos se disponíveis, senão usar fallback
  const themes = themesLoading ? fallbackThemes : (themeTypes.length > 0 ? themeTypes : fallbackThemes);

  // Pacotes de tokens (fallback + dinâmicos)
  type TokenPkg = { amount?: number; price?: string; description?: string; originalPrice?: string };
  const defaultTokenPackages: TokenPkg[] = [
    { amount: 30, price: 'R$ 19,90', description: 'Pacote essencial para começar' },
    { amount: 60, price: 'R$ 34,90', description: 'Melhor custo-benefício' },
    { amount: 90, price: 'R$ 49,90', description: 'Pacote avançado para uso frequente' }
  ];
  const tokenPackages: TokenPkg[] = (themeConfig?.tokens?.items?.length ? themeConfig.tokens.items : defaultTokenPackages) as TokenPkg[];

  const formatPrice = (p?: any) => {
    const s = typeof p === 'number' ? String(p) : String(p ?? '').trim();
    if (!s) return s;
    if (/^R\$/i.test(s) || /^R\$\s*/i.test(s)) return s;
    const cleaned = s.replace(/[^\d.,]/g, '').replace(',', '.');
    const n = Number(cleaned);
    if (!Number.isNaN(n) && cleaned.length) {
      return `R$ ${n.toFixed(2).replace('.', ',')}`;
    }
    return s;
  };

  const defaultFeatures = [
    { icon: <SparklesIcon className="w-7 h-7" />, title: "Tarot Rider-Waite", description: "Leituras com o baralho clássico de 78 cartas. Arcanos Maiores e Menores para visões profundas sobre qualquer área da vida.", image: '' },
    { icon: <StarIcon className="w-7 h-7" />, title: "Astrologia Psicológica", description: "Mapa astral e trânsitos interpretados com foco no autoconhecimento, padrões de comportamento e potencial de vida.", image: '' },
    { icon: <BookOpenIcon className="w-7 h-7" />, title: "Psicologia & Jung", description: "Arquétipos junguianos, integração da sombra e processo de individuação aplicados às leituras de tarot.", image: '' },
    { icon: <EyeIcon className="w-7 h-7" />, title: "Arquétipos e Simbólicos", description: "Estudo dos símbolos universais e arquétipos presentes nas cartas, conectando o inconsciente individual ao coletivo.", image: '' },
    { icon: <HeartIcon className="w-7 h-7" />, title: "Amor e Relacionamentos", description: "Consultas especializadas em vínculos afetivos, padrões de relacionamento, compatibilidade e sinastria.", image: '' },
    { icon: <CurrencyDollarIcon className="w-7 h-7" />, title: "Prosperidade e Propósito", description: "Caminhos para abundância financeira, propósito de vida e alinhamento com sua missão de alma.", image: '' },
    { icon: <HeartIcon className="w-7 h-7" />, title: "Bem Estar e Saúde Emocional", description: "Equilíbrio emocional, cura interior e identificação de bloqueios energéticos que limitam seu bem-estar.", image: '' },
    { icon: <HashtagIcon className="w-7 h-7" />, title: "Numerologia e Kabbalah", description: "Vibração dos números e sabedoria da Árvore da Vida aplicadas à análise do seu caminho e destino.", image: '' },
    { icon: <ScaleIcon className="w-7 h-7" />, title: "Karma e Destino", description: "Compreensão das leis cármicas, lições de vida e padrões de alma que moldam sua jornada de evolução.", image: '' },
    { icon: <SunIcon className="w-7 h-7" />, title: "Caminhos Espirituais", description: "Orientação em práticas espirituais, desenvolvimento da intuição e conexão com a sabedoria superior.", image: '' },
  ];
  const featureIcons = [
    <SparklesIcon className="w-7 h-7" />, <StarIcon className="w-7 h-7" />, <BookOpenIcon className="w-7 h-7" />,
    <EyeIcon className="w-7 h-7" />, <HeartIcon className="w-7 h-7" />, <CurrencyDollarIcon className="w-7 h-7" />,
    <HeartIcon className="w-7 h-7" />, <HashtagIcon className="w-7 h-7" />, <ScaleIcon className="w-7 h-7" />,
    <SunIcon className="w-7 h-7" />,
  ];
  const featuresFromTheme = themeConfig?.features?.items?.length ? themeConfig.features.items.map((it: { title?: string; description?: string; image?: string }, i: number) => ({
    icon: featureIcons[i] ?? featureIcons[0],
    title: it.title || defaultFeatures[i]?.title,
    description: it.description || defaultFeatures[i]?.description,
    image: it.image || defaultFeatures[i]?.image || '',
  })) : defaultFeatures;

  return (
    <div className="min-h-screen bg-gradient-mystical theme-transition theme-bg-background">
      {/* Header com menu móvel */}
      <NavBar />

      {/* Hero Section */}
      <HeroSection 
        backgroundImage={heroImage || undefined}
        title="Desvende os Mistérios do Futuro"
        subtitle="Consultas de tarot personalizadas com interpretação por inteligência artificial. Descubra insights profundos sobre amor, saúde e prosperidade."
        showTextByDefault={true}
      />

      {/* Features Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl font-bold theme-text-primary mb-6">
              {themeConfig?.features?.title || 'POR QUE ESCOLHER NOSSO TAROT?'}
            </h2>
            <p className="text-xl theme-text-secondary max-w-3xl mx-auto">
              {themeConfig?.features?.subtitle || 'Descubra o poder da sabedoria ancestral combinada com tecnologia moderna'}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-5">
            {featuresFromTheme.map((feature, index) => (
              <button
                key={index}
                onClick={() => handleFeatureClick(index)}
                className="group relative overflow-hidden rounded-2xl hover:scale-105 transition-all duration-300 hover:shadow-2xl text-left w-full"
                style={{ aspectRatio: '3/5' }}
              >
                {feature.image ? (
                  <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${feature.image})` }}
                  />
                ) : (
                  <div className="absolute inset-0 theme-card-bg" />
                )}
                <div className="absolute inset-0 transition-colors duration-300" style={{ background: 'rgba(0,0,0,0.20)' }} />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: 'rgba(139,92,246,0.18)' }} />
                <div className="relative z-10 flex flex-col items-center justify-end h-full p-5 pb-8 text-center">
                  <h3 className="text-sm md:text-base font-bold text-white mb-2 leading-tight">{feature.title}</h3>
                  <p className="text-xs md:text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.8)' }}>{feature.description}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Theme Selection Section */}
      <section className="py-20 px-6 bg-black/20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold theme-text-primary mb-6">
              {themeConfig?.selection?.title || 'ESCOLHA SEU TEMA DE CONSULTA'}
            </h2>
            <p className="text-xl theme-text-secondary max-w-3xl mx-auto">
              {themeConfig?.selection?.subtitle || 'Selecione o tema que mais ressoa com suas necessidades atuais'}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {themes.map((theme, index) => {
              const themeImage = themeImages[theme.key as keyof typeof themeImages];
              const imageUrl = toAbs(themeImage);
              
              return (
                <div key={theme.key} className="group relative overflow-hidden rounded-2xl theme-card-bg theme-card-border backdrop-blur-sm hover:scale-105 transition-all duration-300 cursor-pointer">
                  <div className="aspect-[4/3] relative">
                    {imageUrl ? (
                      <div 
                        className="absolute inset-0 bg-cover bg-center"
                        style={{ backgroundImage: `url(${imageUrl})` }}
                      >
                        <div className="absolute inset-0 bg-black/40"></div>
                      </div>
                    ) : (
                      <div className={`bg-gradient-to-br ${theme.gradient}`}></div>
                    )}
                    <div className="relative z-10 flex items-center justify-center h-full">
                      <div className="text-center p-8">
                        <h3 
                          className="text-2xl font-bold mb-4"
                          style={{ color: '#FFFFFF' }}
                        >
                          {theme.name}
                        </h3>
                        <p 
                          className="font-bold"
                          style={{ color: '#FFFFFF' }}
                        >
                          {theme.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* Token Packages Section */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-1/4 w-96 h-96 rounded-full blur-3xl" style={{ background: 'rgba(139,92,246,0.08)' }} />
          <div className="absolute bottom-10 right-1/4 w-96 h-96 rounded-full blur-3xl" style={{ background: 'rgba(245,158,11,0.07)' }} />
        </div>

        <div className="max-w-5xl mx-auto relative">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 rounded-full text-xs font-semibold uppercase tracking-widest mb-5" style={{ background: 'rgba(139,92,246,0.2)', color: '#c4b5fd', border: '1px solid rgba(139,92,246,0.3)' }}>
              ✦ Tokens &amp; Consultas
            </span>
            <h2 className="text-4xl md:text-5xl font-bold theme-text-primary mb-5">
              {themeConfig?.tokens?.title || 'Escolha seu pacote de Tokens'}
            </h2>
            <p className="text-lg theme-text-secondary max-w-2xl mx-auto">
              {themeConfig?.tokens?.subtitle || 'Cada token dá acesso a uma consulta completa com interpretação da Aurora Lyz'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 items-center">
            {tokenPackages.map((pkg: TokenPkg, idx: number) => {
              const isPopular = idx === 1;
              const tierLabels = ['Iniciante', 'Popular', 'Avançado'];
              const tierIcons = ['✦', '★', '◆'];
              const tierFeatures = [
                ['Consultas de tarot', 'Todas as tiragens', 'Interpretação por Aurora Lyz', 'Histórico completo'],
                ['Consultas de tarot', 'Todas as tiragens', 'Interpretação por Aurora Lyz', 'Sinastria incluída', 'Histórico completo'],
                ['Consultas de tarot', 'Todas as tiragens', 'Interpretação por Aurora Lyz', 'Sinastria incluída', 'Pergunta livre à Aurora', 'Histórico completo'],
              ];
              const rawPrice = typeof pkg.price === 'number' ? pkg.price : parseFloat(String(pkg.price || '').replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
              const pricePerToken = pkg.amount && rawPrice ? (rawPrice / pkg.amount).toFixed(2) : null;

              return (
                <div
                  key={idx}
                  className="relative rounded-2xl transition-all duration-300 hover:-translate-y-1"
                  style={isPopular ? {
                    background: 'linear-gradient(145deg, rgba(109,40,217,0.25), rgba(124,58,237,0.15), rgba(245,158,11,0.1))',
                    border: '1px solid rgba(245,158,11,0.45)',
                    boxShadow: '0 0 50px rgba(139,92,246,0.18), 0 20px 40px rgba(0,0,0,0.3)',
                    transform: 'scale(1.04)',
                  } : {
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.09)',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                  }}
                >
                  {isPopular && (
                    <div className="absolute -top-3.5 inset-x-0 flex justify-center">
                      <span className="px-5 py-1 text-xs font-bold rounded-full" style={{ background: 'linear-gradient(to right, #f59e0b, #d97706)', color: '#000', letterSpacing: '0.08em' }}>
                        ★ MAIS POPULAR
                      </span>
                    </div>
                  )}

                  <div className={`p-7 ${isPopular ? 'pt-9' : ''}`}>
                    {/* Tier header */}
                    <div className="text-center mb-6">
                      <div className="text-2xl mb-2" style={{ color: isPopular ? '#f59e0b' : 'rgba(167,139,250,0.8)' }}>{tierIcons[idx]}</div>
                      <div className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: isPopular ? '#fcd34d' : '#a78bfa' }}>
                        {tierLabels[idx]}
                      </div>
                      <div className="text-5xl font-extrabold text-white leading-none mb-1">{pkg.amount}</div>
                      <div className="text-sm text-white/40 mb-4">tokens</div>
                      {pkg.originalPrice && (
                        <div className="flex items-center justify-center gap-2 mb-0.5">
                          <span className="text-sm line-through" style={{ color: 'rgba(255,255,255,0.35)' }}>{formatPrice(pkg.originalPrice)}</span>
                          <span className="px-2 py-0.5 text-[10px] font-bold rounded-full" style={{ background: 'rgba(220,38,38,0.18)', color: '#f87171', letterSpacing: '0.04em' }}>
                            OFERTA
                          </span>
                        </div>
                      )}
                      <div className="text-3xl font-bold" style={{ color: isPopular ? '#fbbf24' : 'var(--color-secondary, #f59e0b)' }}>
                        {formatPrice(pkg.price)}
                      </div>
                      {pkg.originalPrice && (
                        <div className="text-[11px] font-semibold mt-1" style={{ color: '#4ade80' }}>
                          Preço promocional por tempo limitado
                        </div>
                      )}
                      {pricePerToken && (
                        <div className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.3)' }}>
                          R$&nbsp;{pricePerToken}/token
                        </div>
                      )}
                    </div>

                    <div className="h-px mb-6" style={{ background: isPopular ? 'rgba(245,158,11,0.2)' : 'rgba(255,255,255,0.07)' }} />

                    {/* Features */}
                    <ul className="space-y-2.5 mb-7">
                      {(tierFeatures[idx] || []).map((feat, fi) => (
                        <li key={fi} className="flex items-center gap-2.5 text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>
                          <span className="flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: isPopular ? 'rgba(245,158,11,0.2)' : 'rgba(139,92,246,0.2)', color: isPopular ? '#fbbf24' : '#a78bfa' }}>✓</span>
                          {feat}
                        </li>
                      ))}
                    </ul>

                    <Link
                      to={`/checkout?tokens=${pkg.amount}`}
                      onClick={() => setPurchasingIdx(idx)}
                      className={`block text-center py-3.5 px-6 rounded-xl font-semibold text-sm transition-all duration-200 ${purchasingIdx === idx ? 'opacity-50 cursor-not-allowed' : ''}`}
                      style={isPopular ? {
                        background: 'linear-gradient(to right, #f59e0b, #d97706)',
                        color: '#000',
                        fontWeight: 700,
                        boxShadow: '0 4px 20px rgba(245,158,11,0.35)',
                      } : {
                        background: 'rgba(139,92,246,0.2)',
                        color: '#c4b5fd',
                        border: '1px solid rgba(139,92,246,0.35)',
                      }}
                    >
                      {purchasingIdx === idx ? 'Aguarde…' : 'Começar agora'}
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-center gap-6 mt-10 text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
            <span>🔒 Pagamento seguro</span>
            <span>·</span>
            <span>💳 PIX ou Cartão</span>
            <span>·</span>
            <span>⚡ Tokens liberados na hora</span>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-20 px-6"
        style={themeConfig?.ctaColors ? {
          background: `linear-gradient(to right, ${themeConfig.ctaColors.backgroundFrom}, ${themeConfig.ctaColors.backgroundTo})`
        } : { background: 'transparent' }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-4xl font-mystical font-bold theme-text-primary mb-6">
            Pronto para descobrir seu destino?
          </h3>
          <p className="text-xl theme-text-secondary mb-8 font-elegant">
            Junte-se a milhares de pessoas que já transformaram suas vidas com nossas consultas.
          </p>
          <Link 
            to="/register" 
            className="inline-block px-10 py-4 bg-gradient-to-r from-gold-500 to-gold-600 text-white rounded-full text-xl font-semibold hover:from-gold-600 hover:to-gold-700 transition-all transform hover:scale-105 shadow-2xl mystical-glow"
          >
            Começar Agora - Grátis
          </Link>
        </div>
      </section>

      
    </div>
  );
};

export default HomePage;